// Adapter Gmail: unico punto che conosce le colonne di `gmail_messages`,
// la cifratura dei campi e le edge function google-gmail-*.
// Mappa le righe DB (gmail_message_id / gmail_thread_id) su MailMessage
// (message_id / thread_id).

import { supabase } from '../../../supabase';
import { decryptMessage } from '../../../utils/message-crypto';
import type { MailMessage, MailLabel, DraftSummary } from '../types';
import type { MailAdapter, MailModifyAction, MailSendParams, MailDraftParams } from '../mailAdapter';

const SELECT = '*, client:clients(id, business_name, contact_name, logo_url, email)';

// Riga DB → MailMessage (rinomina gmail_message_id/gmail_thread_id).
function mapRow(row: any): MailMessage {
  const { gmail_message_id, gmail_thread_id, ...rest } = row;
  return { ...rest, message_id: gmail_message_id, thread_id: gmail_thread_id } as MailMessage;
}

// Decifra i campi cifrati di un messaggio.
async function decryptMailMessage(msg: MailMessage, organizationId: string): Promise<MailMessage> {
  const [subject, from_name, body_html, body_text] = await Promise.all([
    msg.subject ? decryptMessage(msg.subject, organizationId) : Promise.resolve(msg.subject),
    msg.from_name ? decryptMessage(msg.from_name, organizationId) : Promise.resolve(msg.from_name),
    msg.body_html ? decryptMessage(msg.body_html, organizationId) : Promise.resolve(msg.body_html),
    msg.body_text ? decryptMessage(msg.body_text, organizationId) : Promise.resolve(msg.body_text),
  ]);
  return { ...msg, subject, from_name, body_html, body_text };
}

async function normalize(rows: any[] | null, organizationId: string): Promise<MailMessage[]> {
  const mapped = (rows || []).map(mapRow);
  return Promise.all(mapped.map(m => decryptMailMessage(m, organizationId)));
}

export const gmailAdapter: MailAdapter = {
  provider: 'google',
  storagePrefix: 'gmail_view_mode',

  async fetchMessages(orgId, userId, label, limit) {
    let query = supabase
      .from('gmail_messages')
      .select(SELECT)
      .eq('organization_id', orgId)
      .eq('user_id', userId)
      .order('internal_date', { ascending: false });

    if (label === 'INBOX') {
      query = query.contains('label_ids', ['INBOX']);
    } else if (label === 'SENT') {
      query = query.contains('label_ids', ['SENT']);
    } else if (label === 'STARRED') {
      query = query.eq('is_starred', true);
    } else if (label === 'ALL' || label === 'DRAFT') {
      // 'ALL'/'DRAFT': nessun filtro label ('DRAFT' filtra client-side nella factory),
      // ma il cestino resta escluso — come "Tutti i messaggi" di Gmail.
      query = query.not('label_ids', 'cs', '{TRASH}');
    } else {
      query = query.contains('label_ids', [label]);
    }

    const { data, error } = await query.limit(limit);
    if (error) throw error;
    return normalize(data, orgId);
  },

  async searchMessages(orgId, userId, query) {
    // Ricerca solo sui campi in chiaro (from_email, snippet).
    const sanitized = query.replace(/[%_]/g, '\\$&').replace(/[,.()"']/g, '');
    const { data, error } = await supabase
      .from('gmail_messages')
      .select(SELECT)
      .eq('organization_id', orgId)
      .eq('user_id', userId)
      .or(`from_email.ilike.%${sanitized}%,snippet.ilike.%${sanitized}%`)
      // Il cestino è escluso dalla ricerca, come su Gmail.
      .not('label_ids', 'cs', '{TRASH}')
      .order('internal_date', { ascending: false })
      .limit(200);
    if (error) throw error;
    return normalize(data, orgId);
  },

  async fetchThreadMessages(orgId, userId, threadId) {
    const { data, error } = await supabase
      .from('gmail_messages')
      .select(SELECT)
      .eq('organization_id', orgId)
      .eq('user_id', userId)
      .eq('gmail_thread_id', threadId)
      .order('internal_date', { ascending: true });
    if (error) throw error;
    return normalize(data, orgId);
  },

  async updateReadFlag(orgId, userId, messageRowIds, isRead) {
    await supabase
      .from('gmail_messages')
      .update({ is_read: isRead, updated_at: new Date().toISOString() })
      .eq('organization_id', orgId)
      .eq('user_id', userId)
      .in('id', messageRowIds);
  },

  async updateStarFlag(orgId, userId, threadId, starred) {
    // La stella su Gmail è per-messaggio: si aggiorna la sola riga più recente
    // del thread (la stessa su cui l'edge modify applica star/unstar).
    const { data: latest } = await supabase
      .from('gmail_messages')
      .select('id')
      .eq('organization_id', orgId)
      .eq('user_id', userId)
      .eq('gmail_thread_id', threadId)
      .order('internal_date', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (!latest) return;
    await supabase
      .from('gmail_messages')
      .update({ is_starred: starred, updated_at: new Date().toISOString() })
      .eq('id', latest.id);
  },

  async updateThreadClient(orgId, userId, threadId, clientId) {
    await supabase
      .from('gmail_messages')
      .update({ client_id: clientId, updated_at: new Date().toISOString() })
      .eq('organization_id', orgId)
      .eq('user_id', userId)
      .eq('gmail_thread_id', threadId);
  },

  async sync(orgId, userId, fullSync) {
    const { data, error } = await supabase.functions.invoke('google-gmail-sync', {
      body: { organization_id: orgId, user_id: userId, full_sync: fullSync },
    });
    if (error) throw error;
    return data;
  },

  async fetchFullMessage(orgId, userId, messageId) {
    const { data, error } = await supabase.functions.invoke('google-gmail-message', {
      body: { organization_id: orgId, user_id: userId, message_id: messageId },
    });
    if (error) throw error;
    return data;
  },

  async modify(orgId, userId, messageId, action: MailModifyAction, labelId) {
    // 'move' (sposta in cartella) è specifico di Outlook: Gmail non ha cartelle.
    // Guardia esplicita — MAI raggiungibile dalla UI (l'azione compare solo per Outlook).
    if (action === 'move') {
      throw new Error("Azione 'move' non supportata dal provider Gmail");
    }
    const body: Record<string, any> = {
      organization_id: orgId,
      user_id: userId,
      message_id: messageId,
      action,
    };
    if (labelId !== undefined) body.label_id = labelId;
    const { error } = await supabase.functions.invoke('google-gmail-modify', { body });
    if (error) throw error;
  },

  async send(params: MailSendParams) {
    // params.replyMode ignorato: Gmail non ha un endpoint reply/replyAll/forward
    // separato, l'audience/threading derivano già da to/cc/in_reply_to/references.
    const { error } = await supabase.functions.invoke('google-gmail-send', {
      body: {
        organization_id: params.organizationId,
        user_id: params.userId,
        to: params.to,
        cc: params.cc,
        bcc: params.bcc,
        subject: params.subject,
        body_html: params.bodyHtml,
        thread_id: params.threadId,
        in_reply_to: params.inReplyTo,
        references: params.references,
        attachments: params.attachments,
        track: params.track,
      },
    });
    if (error) throw error;
  },

  async saveDraft(params: MailDraftParams) {
    const { data, error } = await supabase.functions.invoke('google-gmail-draft', {
      body: {
        organization_id: params.organizationId,
        user_id: params.userId,
        draft_id: params.draftId || undefined,
        action: 'save',
        to: params.to,
        cc: params.cc,
        bcc: params.bcc,
        subject: params.subject,
        body_html: params.bodyHtml,
        thread_id: params.threadId,
        in_reply_to: params.inReplyTo,
        references: params.references,
        attachments: params.attachments,
      },
    });
    if (error) throw error;
    return data?.draft_id || null;
  },

  async deleteDraft(orgId, userId, draftId) {
    await supabase.functions.invoke('google-gmail-draft', {
      body: { organization_id: orgId, user_id: userId, draft_id: draftId, action: 'delete' },
    });
  },

  async listDrafts(orgId, userId): Promise<DraftSummary[]> {
    const { data, error } = await supabase.functions.invoke('google-gmail-drafts-list', {
      body: { organization_id: orgId, user_id: userId },
    });
    if (error) throw error;
    return data?.drafts || [];
  },

  async loadLabels(orgId, userId): Promise<MailLabel[]> {
    const { data, error } = await supabase.functions.invoke('google-gmail-labels', {
      body: { organization_id: orgId, user_id: userId },
    });
    if (error) throw error;
    return data?.labels || [];
  },

  async downloadAttachment(orgId, userId, messageId, attachmentId) {
    const { data, error } = await supabase.functions.invoke('google-gmail-attachment', {
      body: { organization_id: orgId, user_id: userId, message_id: messageId, attachment_id: attachmentId },
    });
    if (error) throw error;
    return data.data;
  },
};
